import { useEffect, useState } from 'react';
import { Alert, AlertTitle, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import LockClockRounded from '@mui/icons-material/LockClockRounded';
import { useAuth } from '@/auth';
import { AuthLayout } from '@/layouts/AuthLayout';
import { AuthHeader } from '@/pages/auth/AuthHeader';
import { STATUS } from './billing/labels';
import { MyBilling } from './billing/MyBilling';
import { PageShell } from './billing/PageShell';
import { useSession } from './session';

/**
 * Shown instead of the app when none of the person's institutions may use the platform
 * (unpaid, canceled, courtesy ended, cut off). Coordinators see the plan and pay right here;
 * everyone else is told to talk to the coordination. Moves on by itself once access is back.
 */
export function AccessBlockedPage() {
  const { signOut } = useAuth();
  const { me, refresh } = useSession();
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    const onFocus = () => void refresh();
    window.addEventListener('focus', onFocus);
    const poll = setInterval(() => void refresh(), 30_000);
    return () => {
      window.removeEventListener('focus', onFocus);
      clearInterval(poll);
    };
  }, [refresh]);

  const names = me.blockedInstitutions.map((i) => i.name).join(', ');

  if (me.managesBilling) {
    return (
      <PageShell plain onSignOut={() => void signOut()}>
        <Stack spacing={1} sx={{ mb: 3 }}>
          <Typography variant="h4" component="h1">
            O acesso de {names} está parado
          </Typography>
          <Typography color="text.secondary">
            Estudantes e professores não conseguem entrar até o plano ser regularizado. Nenhum dado
            foi apagado: assim que o pagamento for confirmado, tudo volta como estava.
          </Typography>
        </Stack>
        <MyBilling onlyBlocked onChanged={() => void refresh()} />
      </PageShell>
    );
  }

  const status = me.blockedInstitutions[0]?.status;
  return (
    <AuthLayout>
      <AuthHeader title="Acesso indisponível" />
      <Alert severity="warning" icon={<LockClockRounded />} sx={{ mb: 3 }}>
        <AlertTitle>
          {names}:{' '}
          {status ? STATUS[status as keyof typeof STATUS]?.label.toLowerCase() : 'sem acesso'}
        </AlertTitle>
        O acesso da sua instituição ao Lousa está suspenso no momento. Seus dados e resultados
        continuam guardados. Fale com a coordenação do seu curso.
      </Alert>
      <Stack spacing={1.5}>
        <Button
          variant="contained"
          size="large"
          disabled={checking}
          onClick={async () => {
            setChecking(true);
            await refresh();
            setChecking(false);
          }}
        >
          {checking ? <CircularProgress size={22} color="inherit" /> : 'Verificar de novo'}
        </Button>
        <Button color="inherit" onClick={() => void signOut()}>
          Sair
        </Button>
      </Stack>
      <Box sx={{ mt: 3 }} />
    </AuthLayout>
  );
}
